import {
  CreditEffectOptions,
  EmailTemplateTypes,
  PolicyIntervalType,
  PolicyStatementType,
  StatementRelativePeriods,
  getOrdinalSuffix,
  policyWeeklyDayIntervalMap,
  accountingSystemSpecificTerm,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from 'shared';
import { useMemo } from 'react';
import { When } from 'react-if';

import { InputTextAddon } from '../../../../../common/Atoms/InputTextAddon';
import { fieldIsValid } from '../../../../lib/helper';
import { Grid } from '../../../../../common/Atoms/Grid';
import { GridItem } from '../../../../../common/Atoms/GridItem';
import { Select } from '../../../../../common/Atoms/Select';
import { IPolicyProps } from '../PolicyItem';
import { Transition } from '../../../../../common/Atoms/Transition';
import { EmailTemplateSelector } from '../PolicyForm/Fields/EmailTemplateSelector';
import { Toggle } from '../../../../../common/Atoms/Toggle';
import { Badge } from '../../../../../common/Atoms/Badge';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { MinimumDue } from '../PolicyForm/Fields/MinimumDue';
import { useSettings } from '../../../../hooks/useSettings';
import { Notification } from '../../../../../common/Atoms/Notification';
import { SenderProfileSelector } from '../PolicyForm/SenderProfileSelector';
import { StatementTemplateSelector } from '../PolicyForm/Fields/StatementTemplateSelector';

const intervalOptions = [
  {
    label: `Monthly`,
    value: PolicyIntervalType.MONTHLY,
  },
  {
    label: `Monthly (Weekdays)`,
    value: PolicyIntervalType.MONTHLY_WEEKDAYS,
  },
  {
    label: `Weekly`,
    value: PolicyIntervalType.WEEKLY,
  },
];

const typeOptions = [
  {
    label: `Outstanding - Send only to contacts with an outstanding balance`,
    value: PolicyStatementType.OUTSTANDING,
  },
  {
    label: `Overdue - Send only to contacts with an overdue balance`,
    value: PolicyStatementType.OVERDUE,
  },
  {
    label: `All - Send to all customers even those with no balance`,
    value: PolicyStatementType.ALL,
  },
  {
    label: `In Credit - Send to contacts with a negative balance`,
    value: PolicyStatementType.IN_CREDIT,
  },
];

const periodOptions = [
  {
    label: `All Outstanding Items`,
    value: StatementRelativePeriods.ALL_TIME,
  },
  {
    label: `View all outstanding & future`,
    value: StatementRelativePeriods.ALL_TIME_INC_FUTURE,
  },
  {
    label: `Issued before current month`,
    value: StatementRelativePeriods.UPTO_PREVIOUS_MONTH,
  },
  {
    label: `Issued only in the previous month`,
    value: StatementRelativePeriods.PREVIOUS_MONTH,
  },
  {
    label: `Issued only in the current month`,
    value: StatementRelativePeriods.THIS_MONTH,
  },
];

export function PolicyStatement({
  policy,
  invalids,
  handleChange,
}: IPolicyProps) {
  const currentOrg = useGetSelectedOrganisation();
  const { data: settings } = useSettings();
  const intervalValues = useMemo(() => {
    if (!policy) return [];

    if (policy.interval_type === PolicyIntervalType.MONTHLY || policy.interval_type === PolicyIntervalType.MONTHLY_WEEKDAYS) {
      return Array.from(Array(31).keys()).map(i => ({
        label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
        value: i + 1,
      }));
    }

    return Object.keys(policyWeeklyDayIntervalMap).map(key => ({
      label: policyWeeklyDayIntervalMap[key],
      value: Number(key),
    }));

  }, [policy?.interval_type]);

  const inCreditSettingsWarning = useMemo(() => {
    if (!settings) return null;

    if (policy.statement_type !== PolicyStatementType.IN_CREDIT) return null;

    const warningStr = `You currently have the following configured as "Ignored" in Advanced Settings: `;
    const types: string[] = [];
    if (settings.creditNoteEffect === CreditEffectOptions.Ignore) {
      types.push(accountingSystemSpecificTerm(currentOrg, `Credit Notes`));
    }
    if (accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_PREPAYMENTS)) {
      if (settings.prepaymentsEffect === CreditEffectOptions.Ignore) {
        types.push(`Prepayments`);
      }

      if (settings.overpaymentsEffect === CreditEffectOptions.Ignore) {
        types.push(`Overpayments`);
      }
    }

    if (types.length === 0) return null;

    return warningStr + types.join(`, `) + `.`;
  }, [settings, policy.statement_type, currentOrg?.accountingSystemType]);

  return (
    <>
      <Grid cols={ 3 }>
        <GridItem span={ 1 }
          position={ `bottom` }
        >
          <Select
            label={ `When should this statement be sent?` }
            options={ intervalOptions }
            onChange={ handleChange }
            selected={ policy.interval_type }
            name={ `interval_type` }
          />
        </GridItem>
        <GridItem span={ 1 }
          position={ `bottom` }>
          <Select
            label={ `Which day of the month?` }
            options={ intervalValues }
            onChange={ handleChange }
            selected={ policy.day_index }
            name={ `day_index` }
            helpIcon={ policy.interval_type === PolicyIntervalType.MONTHLY_WEEKDAYS
              ? `If the day falls on a weekend or public holiday, it will be sent on the next business day`
              : `If the day of the month is greater than the number of days in the month, the last day of the month will be used` }
          />
        </GridItem>

        <GridItem span={ 1 }
          position={ `bottom` }>
          <Select
            label={ `Outstanding Statement Period` }
            options={ periodOptions }
            onChange={ handleChange }
            selected={ policy.statement_period }
            name={ `statement_period` }
            helpIcon={
              `Only applies to outstanding statements. Which Invoices & Credits to include in the statement. \n Current and previous months are relative to the day in which the statement is sent. Based on Issue Date.`
            }
          />
        </GridItem>
      </Grid>

      <Grid cols={ 12 }>
        <GridItem span={ 12 }>
          <Select
            label={ `Statement Trigger Type` }
            options={ typeOptions }
            onChange={ handleChange }
            selected={ policy.statement_type }
            name={ `statement_type` }
            helpIcon={ `Will send statements to contacts where any currency meets the criteria` }
          />
        </GridItem>
      </Grid>

      <Transition
        show={ policy.statement_type === PolicyStatementType.OVERDUE }
        speed={ `slow` }
      >
        <div>
          <Grid cols={ 2 }>
            <GridItem span={ 1 }>
              <InputTextAddon
                dataType={ `number` }
                value={ policy.overdue_age_min || 0 }
                onChange={ handleChange }
                name={ `overdue_age_min` }
                label={ `Minimum Overdue Age` }
                description={ `Skip sending if all invoices are overdue by fewer than this many days` }
                invalid={ fieldIsValid(`overdue_age_min`, invalids) }
                addOnText={ `days` }
              />
            </GridItem>
            <GridItem span={ 1 }>
              <InputTextAddon
                dataType={ `number` }
                value={ policy.overdue_age_max || `` }
                onChange={ handleChange }
                name={ `overdue_age_max` }
                label={ `Maximum Overdue Age` }
                description={ `Skip sending if any invoices are overdue by more than this many days` }
                invalid={ fieldIsValid(`overdue_age_max`, invalids) }
                addOnText={ `days` }
              />
            </GridItem>
          </Grid>
        </div>
      </Transition>

      <Transition
        show={ policy.statement_type === PolicyStatementType.OUTSTANDING || policy.statement_type === PolicyStatementType.OVERDUE }
        speed={ `slow` }
      >
        <div>
          <Grid cols={ 3 }>
            <GridItem span={ 1 }>
              <MinimumDue
                policyFormData={ policy }
                invalids={ invalids }
                handleChange={ handleChange }
              />
            </GridItem>
          </Grid>
        </div>
      </Transition>

      <Grid cols={ 1 }>
        <GridItem span={ 1 }>
          <EmailTemplateSelector
            value={ policy.template_id }
            handleChange={ handleChange }
            type={ EmailTemplateTypes.STATEMENT_EMAIL }
            invalid={ fieldIsValid(`template_id`, invalids) }
            label={ `Email template` }
            emptyText={ `Select Template` }
            nullable
            name={ `template_id` }
            currentPolicyId={ policy.id }
          />
        </GridItem>

        <GridItem span={ 1 }>
          <SenderProfileSelector
            handleChange={ handleChange }
            policyFormData={ policy }
            invalids={ invalids }
          />
        </GridItem>
      </Grid>

      <Toggle
        label={ `Attach outstanding statement as PDF` }
        checked={ policy.include_statement_pdf || false }
        onChange={ handleChange }
        name={ `include_statement_pdf` }
        description={ `Attaches a generated PDF outstanding statement to the email` }
      />

      <When condition={ policy.include_statement_pdf }>
        <StatementTemplateSelector
          policy={ policy }
          handleChange={ handleChange }
        />
      </When>

      <When condition={ accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT) }>
        <Toggle
          label={ (
            <span className={ `flex` }>
              { `Attach activity statement as PDF` }
              <Badge
                color={ `purple` }
                className={ `ml-1.5` }
              >
                { `New` }
              </Badge>

            </span>
          ) }
          checked={ policy.include_activity_pdf || false }
          onChange={ handleChange }
          name={ `include_activity_pdf` }
          description={ `Attaches a generated PDF activity statement to the email using the default date period.` }
          helpIcon={ `You can edit the default date period in organisation settings. Ability to override the dates here is coming soon!` }
        />
      </When>

      <When condition={ !!inCreditSettingsWarning }>
        { () => {
          return (
            <Notification
              type={ `warning` }
            >
              { inCreditSettingsWarning }
            </Notification>
          );
        } }
      </When>
    </>
  );
}
